// Calendar helpers. Dates are stored as plain ISO days (YYYY-MM-DD) and handled in UTC,
// so a roadmap shows the same sprint dates in every time zone.

export const DEFAULT_SPRINT_LENGTH = 14;
export const MIN_SPRINT_LENGTH = 1;
export const MAX_SPRINT_LENGTH = 56;
export const SPRINT_LENGTH_OPTIONS = [7, 14, 21, 28];

const DAY = 86400000;
const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isIsoDay(value) {
  if (typeof value !== 'string') return false;
  const m = ISO_DAY.exec(value);
  if (!m) return false;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return d.getUTCFullYear() === +m[1] && d.getUTCMonth() === +m[2] - 1 && d.getUTCDate() === +m[3];
}

export function parseDay(value) {
  if (!isIsoDay(value)) return null;
  const [y, m, d] = value.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

export function toIsoDay(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
}

export function todayIso(now = new Date()) {
  return toIsoDay(new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())));
}

export function addDays(iso, days) {
  const d = parseDay(iso);
  if (!d) return null;
  return toIsoDay(new Date(d.getTime() + days * DAY));
}

export function mondayOf(iso) {
  const d = parseDay(iso);
  if (!d) return null;
  const offset = (d.getUTCDay() + 6) % 7;
  return toIsoDay(new Date(d.getTime() - offset * DAY));
}

export function hasDates(roadmap) {
  return isIsoDay(roadmap?.settings?.startDate);
}

function halfSprintMs(settings) {
  return ((settings?.sprintLengthDays || DEFAULT_SPRINT_LENGTH) / 2) * DAY;
}

/** Date at the start of a half-sprint slot (slot may be fractional). */
export function slotToDate(settings, slot) {
  const start = parseDay(settings?.startDate);
  if (!start) return null;
  return new Date(start.getTime() + slot * halfSprintMs(settings));
}

/** Fractional slot position of a date on the roadmap's timeline. */
export function dateToSlot(settings, iso) {
  const start = parseDay(settings?.startDate);
  const d = typeof iso === 'string' ? parseDay(iso) : iso;
  if (!start || !d) return null;
  return (d.getTime() - start.getTime()) / halfSprintMs(settings);
}

export function sprintStartDate(settings, sprint) {
  return slotToDate(settings, (sprint - 1) * 2);
}

/** Last day of a sprint (inclusive). */
export function sprintEndDate(settings, sprint) {
  const next = slotToDate(settings, sprint * 2);
  return next ? new Date(next.getTime() - DAY) : null;
}

/** Inclusive date range covered by slots [start, start + span). */
export function rangeDates(settings, start, span) {
  const from = slotToDate(settings, start);
  const to = slotToDate(settings, start + span);
  if (!from || !to) return null;
  return { from, to: new Date(to.getTime() - DAY) };
}

const fmtShort = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const fmtLong = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

export function formatDay(date, { year = false } = {}) {
  if (!date) return '';
  const d = typeof date === 'string' ? parseDay(date) : date;
  if (!d) return '';
  return (year ? fmtLong : fmtShort).format(d);
}

export function formatDateRange(range) {
  if (!range) return '';
  const sameYear = range.from.getUTCFullYear() === range.to.getUTCFullYear();
  return `${formatDay(range.from, { year: !sameYear })} to ${formatDay(range.to, { year: true })}`;
}

/** Fractional slot for "now", or null when the roadmap has no dates or today is off the timeline. */
export function todaySlot(roadmap, total, now = new Date()) {
  if (!hasDates(roadmap)) return null;
  const slot = dateToSlot(roadmap.settings, todayIso(now));
  if (slot === null) return null;
  const withinDay = (now.getHours() * 60 + now.getMinutes()) / 1440;
  const value = slot + withinDay / ((roadmap.settings.sprintLengthDays || DEFAULT_SPRINT_LENGTH) / 2);
  return value >= 0 && value <= total ? value : null;
}

export function currentSprint(roadmap, now = new Date()) {
  const total = (roadmap?.settings?.sprintCount || 0) * 2;
  const slot = todaySlot(roadmap, total, now);
  return slot === null ? null : Math.min(roadmap.settings.sprintCount, Math.floor(slot / 2) + 1);
}
